import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { fetchPersonSearch } from '../features/people/people-api';

/**
 * Wyszukiwarka w nagłówku (audyt UI, znalezisko 11 — „Pole wyszukiwania w nagłówku myląco
 * twierdzi, że funkcji nie ma, choć ona działa”). Dotąd pole było trwale `disabled` z
 * placeholderem „(dostępne w kolejnym etapie)”, mimo że `GET /api/v1/search` już działa i jest
 * używane przez `/people` (`features/people/PeopleListRoute.tsx`).
 *
 * TEN PLIK WOŁA WYŁĄCZNIE ISTNIEJĄCĄ FUNKCJĘ API (`fetchPersonSearch`, `../features/people/
 * people-api.ts`). „Pokaż wszystkie wyniki” nawiguje do `/people?q=<fraza>` — `router.tsx`
 * (`validatePeopleSearch`) niesie frazę w query string, a `PeopleListRoute.tsx` czyta ją jako
 * wartość początkową swojego własnego pola wyszukiwania (dalej działa identycznie bez tego
 * parametru, np. przy bezpośrednim wejściu na `/people`).
 *
 * PEŁNY WZORZEC COMBOBOX ARIA 1.2 (§19.2 „pełna obsługa klawiatury”): `role="combobox"` na
 * polu, `role="listbox"`/`role="option"` na podpowiedziach, `aria-activedescendant` zamiast
 * przenoszenia fokusu (fokus zawsze zostaje w polu tekstowym — standardowy wzorzec combobox),
 * zamykanie przez Escape i kliknięcie poza komponentem (nasłuch `mousedown` na `document`,
 * ograniczony do czasu, gdy lista jest otwarta).
 */

const SEARCH_DEBOUNCE_MS = 300;
const MAX_SUGGESTIONS = 8;

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(handle);
    };
  }, [value, delayMs]);
  return debounced;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const reactId = useId();
  const listboxId = `global-search-listbox-${reactId}`;

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(trimmedQuery, SEARCH_DEBOUNCE_MS);

  const searchQuery = useQuery({
    queryKey: ['global-search', debouncedQuery] as const,
    queryFn: ({ signal }) => fetchPersonSearch({ q: debouncedQuery, signal }),
    enabled: debouncedQuery.length > 0,
  });

  const results = (searchQuery.data?.family ?? []).slice(0, MAX_SUGGESTIONS);
  // Ostatnia pozycja listy jest zawsze „Pokaż wszystkie wyniki” (gdy jest o co pytać) — ten sam
  // indeks przestrzeni co pozycje wyników, więc strzałki/Enter obsługują ją bez specjalnego kodu.
  const showAllIndex = results.length;
  const optionCount = debouncedQuery.length > 0 ? results.length + 1 : 0;

  function optionId(index: number): string {
    return `${listboxId}-option-${String(index)}`;
  }

  // Nowe zapytanie/nowe wyniki → podświetl pierwszą pozycję (typowy wzorzec „Enter trafia w
  // najlepsze dopasowanie”, §5) zamiast zostawiać zaznaczenie z poprzedniego stanu.
  useEffect(() => {
    setActiveIndex(optionCount > 0 ? 0 : null);
  }, [debouncedQuery, results.length, optionCount]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function handlePointerDown(event: MouseEvent): void {
      if (containerRef.current !== null && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen]);

  function closeDropdown(): void {
    setIsOpen(false);
    setActiveIndex(null);
  }

  function goToPerson(personId: string): void {
    setQuery('');
    closeDropdown();
    void navigate({ to: '/people/$id', params: { id: personId } });
  }

  function goToAllResults(): void {
    const q = trimmedQuery;
    closeDropdown();
    void navigate(q.length > 0 ? { to: '/people', search: { q } } : { to: '/people' });
  }

  function selectOptionAt(index: number): void {
    if (index === showAllIndex) {
      goToAllResults();
      return;
    }
    const result = results[index];
    if (result !== undefined) {
      goToPerson(result.personId);
    }
  }

  function handleChange(value: string): void {
    setQuery(value);
    setIsOpen(value.trim().length > 0);
  }

  function handleFocus(): void {
    if (trimmedQuery.length > 0) {
      setIsOpen(true);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      if (optionCount > 0) {
        setActiveIndex((previous) => {
          const next = previous === null ? 0 : previous + 1;
          return next >= optionCount ? 0 : next;
        });
      }
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (optionCount > 0) {
        setActiveIndex((previous) => {
          const next = previous === null ? optionCount - 1 : previous - 1;
          return next < 0 ? optionCount - 1 : next;
        });
      }
      return;
    }
    if (event.key === 'Enter') {
      if (isOpen && activeIndex !== null) {
        event.preventDefault();
        selectOptionAt(activeIndex);
      } else if (trimmedQuery.length > 0) {
        event.preventDefault();
        goToAllResults();
      }
      return;
    }
    if (event.key === 'Escape') {
      if (isOpen) {
        event.preventDefault();
        closeDropdown();
      }
      return;
    }
  }

  const activeOptionId = isOpen && activeIndex !== null ? optionId(activeIndex) : undefined;
  const showDropdown = isOpen && debouncedQuery.length > 0;

  let statusText = '';
  if (debouncedQuery.length > 0) {
    if (searchQuery.isPending) {
      statusText = 'Szukam…';
    } else if (searchQuery.isError) {
      statusText = 'Nie udało się wyszukać osób.';
    } else {
      statusText = `Znaleziono wyników: ${String(results.length)}.`;
    }
  }

  return (
    <div ref={containerRef} className="relative min-w-40 max-w-sm flex-1">
      <label htmlFor="global-search" className="sr-only">
        Szukaj osoby
      </label>
      <input
        ref={inputRef}
        id="global-search"
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
        aria-autocomplete="list"
        autoComplete="off"
        value={query}
        onChange={(event) => {
          handleChange(event.target.value);
        }}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder="Szukaj osoby po imieniu i nazwisku…"
        className="h-11 w-full rounded border border-border-strong bg-bg px-3 text-sm text-ink"
      />
      {/* `aria-live="polite"` BEZ jawnej roli `status` (§19.3, ten sam wzorzec co
          `RelationshipPathControls.tsx`, patrz jego komentarz) — CELOWO: strona już ma inne,
          WŁASNE regiony `role="status"` (np. `TreeCanvas.tsx`/`TreeList.tsx`), a ten pasek jest
          zamontowany globalnie w nagłówku na KAŻDEJ trasie chronionej, więc drugi, niezależny
          `role="status"` łamałby założenie „dokładnie jeden taki element na stronie” przyjęte
          przez inne testy (`getByRole('status')` bez filtra nazwy — np. `BackupsTab.test.tsx`).
          `aria-live="polite"` samo w sobie WYSTARCZA do ogłoszenia zmiany treści czytnikom
          ekranu, bez deklarowania drugiej roli „status” na tej samej stronie. Element zostaje
          NA STAŁE zamontowany (nie warunkowo), bo część czytników ekranu ogłasza zmiany regionu
          `aria-live` wyłącznie, gdy region istniał w DOM już PRZED zmianą treści. */}
      <span className="sr-only" aria-live="polite">
        {statusText}
      </span>

      {showDropdown ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Podpowiedzi osób"
          className="absolute inset-x-0 top-full z-50 mt-1 max-h-80 overflow-auto rounded border border-border bg-surface text-sm shadow-lg"
        >
          {searchQuery.isPending ? <li className="px-3 py-2 text-ink-muted">Szukam…</li> : null}
          {searchQuery.isError ? (
            <li className="px-3 py-2 text-danger">Nie udało się wyszukać osób.</li>
          ) : null}
          {!searchQuery.isPending && !searchQuery.isError && results.length === 0 ? (
            <li className="px-3 py-2 text-ink-muted">Brak osób pasujących do wyszukiwania.</li>
          ) : null}
          {results.map((result, index) => (
            <li
              key={result.personId}
              id={optionId(index)}
              role="option"
              aria-selected={activeIndex === index}
              onClick={() => {
                goToPerson(result.personId);
              }}
              onMouseEnter={() => {
                setActiveIndex(index);
              }}
              className={`flex min-h-11 cursor-pointer flex-col justify-center px-3 py-2 ${
                activeIndex === index ? 'bg-bg' : ''
              }`}
            >
              <span className="font-medium text-ink">{result.displayName}</span>
              <span className="text-xs text-ink-muted">{result.matchReason}</span>
            </li>
          ))}
          <li
            id={optionId(showAllIndex)}
            role="option"
            aria-selected={activeIndex === showAllIndex}
            onClick={goToAllResults}
            onMouseEnter={() => {
              setActiveIndex(showAllIndex);
            }}
            className={`flex min-h-11 cursor-pointer items-center border-t border-border px-3 py-2 font-medium text-accent ${
              activeIndex === showAllIndex ? 'bg-bg' : ''
            }`}
          >
            Pokaż wszystkie wyniki
          </li>
        </ul>
      ) : null}
    </div>
  );
}
